import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { X, User, Warning, CheckCircle } from "@phosphor-icons/react";

/**
 * Sleek modal for creating a new customer quickly, with live phone duplicate detection.
 * - onCreated(newCustomer) fires when a brand-new customer is saved
 * - onSelectExisting(existingCustomer) fires when the user clicks "Use this" on a match
 */
export default function AddCustomerModal({ open, onClose, onCreated, onSelectExisting, initialPhone = "" }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", abn: "", company: "", address: "" });
  const [showBusiness, setShowBusiness] = useState(false);
  const [matches, setMatches] = useState([]);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ name: "", phone: initialPhone || "", email: "", abn: "", company: "", address: "" });
      setShowBusiness(false);
      setMatches([]);
    }
  }, [open, initialPhone]);

  const normalize = (p) => (p || "").replace(/[^0-9+]/g, "");

  useEffect(() => {
    if (!open) return;
    const n = normalize(form.phone);
    if (n.length < 4) { setMatches([]); return; }
    setChecking(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: { q: form.phone.trim() } });
        const exact = data.filter((c) => {
          const cp = normalize(c.phone);
          return cp && (cp === n || cp.endsWith(n) || n.endsWith(cp));
        });
        setMatches(exact.slice(0, 3));
      } catch { setMatches([]); }
      finally { setChecking(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [form.phone, open]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (matches.length > 0) return toast.error("Customer with this phone already exists");
    setSaving(true);
    try {
      const { data } = await api.post("/customers", form);
      toast.success("Customer added");
      onCreated?.(data);
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const pick = (c) => {
    onSelectExisting?.(c);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="add-customer-modal-overlay"
    >
      <div
        className="bg-white w-full max-w-md rounded-[24px] shadow-2xl shadow-slate-900/20 border border-slate-200/60 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-customer-modal"
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#1E5FEB] to-[#1849C8] flex items-center justify-center shadow-md shadow-blue-500/25">
              <User size={16} weight="bold" className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">NEW CUSTOMER</div>
              <h3 className="font-heading font-bold text-[18px] tracking-tight text-slate-900 leading-tight">
                Add to address book
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition-colors"
            data-testid="add-customer-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="px-6 pb-5 space-y-3">
          <Field label="Name">
            <input
              autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={inputCls}
              placeholder="Full name"
              data-testid="add-customer-name"
            />
          </Field>

          <Field label="Phone">
            <input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className={inputCls + " font-mono"}
              placeholder="+61 …"
              data-testid="add-customer-phone"
            />
          </Field>

          {matches.length > 0 && (
            <div
              className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2.5"
              data-testid="add-customer-dup-warning"
            >
              <Warning size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold text-amber-900">
                  Customer already exists
                </div>
                <div className="text-[11px] text-amber-800 mt-0.5">
                  A customer with this phone number is already in your address book.
                </div>
                <div className="mt-2 space-y-1">
                  {matches.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => pick(m)}
                      className="w-full flex items-center justify-between bg-white hover:bg-amber-100/60 border border-amber-200 rounded-lg px-2.5 py-1.5 text-left transition-colors"
                      data-testid={`add-customer-use-existing-${m.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-[13px] font-medium text-slate-800 truncate">{m.name}</div>
                        <div className="text-[11px] font-mono text-slate-500">{m.phone}</div>
                      </div>
                      <span className="text-[10px] font-bold tracking-wider text-amber-700 shrink-0 ml-2">
                        USE THIS <CheckCircle size={10} weight="fill" className="inline -mt-0.5 ml-0.5" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {checking && matches.length === 0 && normalize(form.phone).length >= 4 && (
            <div className="text-[11px] text-slate-400 font-mono">checking duplicates…</div>
          )}

          <Field label="Email (optional)">
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
              placeholder="name@example.com"
            />
          </Field>

          {/* Business customer toggle — keeps the default form clean */}
          <div className="pt-2">
            <button
              type="button"
              onClick={() => setShowBusiness((v) => !v)}
              data-testid="toggle-business-fields"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#1E5FEB] hover:underline tracking-wide"
            >
              {showBusiness ? "— Hide business fields" : "+ Business customer (ABN, company, address)"}
            </button>
          </div>

          {showBusiness && (
            <div className="space-y-3 pt-1 border-t border-slate-100 -mx-6 px-6 pt-4">
              <Field label="Company name">
                <input
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className={inputCls}
                  placeholder="e.g. Acme Pty Ltd"
                  data-testid="add-customer-company"
                />
              </Field>
              <Field label="ABN">
                <input
                  value={form.abn}
                  onChange={(e) => setForm({ ...form, abn: e.target.value })}
                  className={inputCls + " font-mono"}
                  placeholder="12 345 678 901"
                  inputMode="numeric"
                  data-testid="add-customer-abn"
                />
              </Field>
              <Field label="Billing address">
                <textarea
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  rows={2}
                  className={inputCls + " h-auto py-2 resize-none"}
                  placeholder="Street · Suburb · State · Postcode"
                  data-testid="add-customer-address"
                />
              </Field>
            </div>
          )}
        </div>

        <div className="flex gap-2 px-6 pb-5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-700 transition-colors"
            data-testid="add-customer-cancel"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !form.name.trim() || matches.length > 0}
            className="flex-1 h-11 rounded-xl bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#123a9e] disabled:opacity-40 disabled:from-slate-300 disabled:to-slate-400 text-white text-[13px] font-semibold transition-all shadow-md shadow-blue-500/25"
            data-testid="add-customer-save"
          >
            {saving ? "SAVING…" : "SAVE CUSTOMER"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  "w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm bg-white transition-all";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}
